import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

const STATUS_STYLE = {
    ACTIVE: "bg-emerald-100 text-emerald-600 border-emerald-500/30",
    PROTOTYPE: "bg-cyan-100 text-cyan-600 border-cyan-500/30",
    EXPERIMENTAL: "bg-purple-100 text-purple-600 border-purple-500/30",
    "SURPRISINGLY WORKING": "bg-amber-100 text-amber-600 border-amber-500/30",
    "TEMPORARILY BROKEN": "bg-red-100 text-red-600 border-red-500/30",
    "AWAITING PARTS": "bg-slate-200/70 text-slate-500 border-slate-500/30",
};

const ROTATING = [
    "ACTIVE",
    "PROTOTYPE",
    "EXPERIMENTAL",
    "TEMPORARILY BROKEN",
    "SURPRISINGLY WORKING",
    "AWAITING PARTS",
    "WHY IS THIS WORKING?",
];

const EXPERIMENTS = [
    {
        id: "EXP_01",
        name: "TIMBOT",
        blurb: "Autonomous digital agent.",
        status: "ACTIVE",
        footer: "HARNESS: BUILT FROM SCRATCH",
        dossier: [
            ["ARCHITECTURE", "LIGHTWEIGHT HARNESS"],
            ["MEMORY", "PERSISTENT"],
            ["LAST INCIDENT", "TEAPOT-RELATED"],
        ],
    },
    {
        id: "EXP_02",
        name: "ROBOT 001",
        blurb: "Friendly physical robot prototype.",
        status: "PROTOTYPE",
        footer: "ARCH: RASPBERRY PI",
        dossier: [
            ["ARMS", "LOBSTER-ADJACENT"],
            ["CLOUD", "PREFERABLY NONE"],
            ["ANNOYANCE CEILING", "LOW (BY DESIGN)"],
        ],
    },
    {
        id: "EXP_03",
        name: "LOCAL AI",
        blurb:
            "Experiments involving running unnecessarily large language models on hardware that would prefer Tim stopped doing that.",
        status: "EXPERIMENTAL",
        footer: "ENGINE: QUANTIZED GGUF",
        dossier: [
            ["VRAM", "INSUFFICIENT (IGNORED)"],
            ["HARDWARE FEELINGS", "HURT"],
            ["TOKENS/SEC", "SPIRITUALLY"],
        ],
    },
    {
        id: "EXP_04",
        name: "LOCAL DICTATION",
        blurb:
            "Private local speech-to-text software designed for professional and clinical environments.",
        status: "PROTOTYPE",
        footer: "PRIVACY: 100% OFFLINE",
        dossier: [
            ["AUDIO LEAVES DEVICE", "NEVER"],
            ["LATENCY", "LOCAL"],
            ["CLOUD PHONES HOME", "IT DOESN'T"],
        ],
    },
    {
        id: "EXP_05",
        name: "3D FABRICATION",
        blurb: "CAD, Blender, FreeCAD, procedural modelling and 3D printing experiments.",
        status: "ACTIVE",
        footer: "FAB: PETG / RESIN",
        dossier: [
            ["FAILED PRINTS", "ARCHIVED WITH DIGNITY"],
            ["BLENDER CRASHES", "CEREMONIAL"],
            ["CALIPERS", "WITHIN REACH"],
        ],
    },
    {
        id: "EXP_06",
        name: "HOME AUTOMATION",
        blurb:
            "An increasingly elaborate system originally intended to turn the coffee machine on.",
        status: "SURPRISINGLY WORKING",
        footer: "BUS: MQTT / HOME ASSISTANT",
        dossier: [
            ["ORIGINAL GOAL", "COFFEE AT SUNRISE"],
            ["CURRENT SCOPE", "UNCLEAR"],
            ["ROLLBACK PLAN", "NONE"],
        ],
    },
];

export default function ExperimentsSection() {
    const [open, setOpen] = useState(null);
    const [rotIdx, setRotIdx] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setRotIdx((i) => (i + 1) % ROTATING.length), 2000);
        return () => clearInterval(id);
    }, []);

    return (
        <section id="experiments" className="py-20 border-b border-subtle bg-[#f2f4f6]" data-testid="experiments-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <span className="font-mono text-xs text-cyan-600 tracking-widest uppercase">
                            // Experimental Index
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">The Lab</h2>
                    </div>
                    <div className="font-mono text-[11px] text-slate-500">
                        STATUS ROTATION:{" "}
                        <span data-testid="status-rotator" className="text-amber-600">{ROTATING[rotIdx]}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {EXPERIMENTS.map((exp, i) => {
                        const expanded = open === exp.id;
                        return (
                            <button
                                key={exp.id}
                                type="button"
                                onClick={() => setOpen(expanded ? null : exp.id)}
                                data-testid={`experiment-card-${i + 1}`}
                                className={`text-left p-6 rounded-lg bg-white border flex flex-col justify-between transition-colors duration-300 focus:outline-none ${
                                    expanded ? "border-cyan-500/50" : "border-subtle hover:border-cyan-500/30"
                                }`}
                            >
                                <div className="w-full">
                                    <div className="flex items-center justify-between mb-4 font-mono text-xs">
                                        <span className="text-slate-500">{exp.id}</span>
                                        <span className={`px-2 py-0.5 rounded border ${STATUS_STYLE[exp.status]}`}>
                                            {exp.status}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">{exp.name}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed">{exp.blurb}</p>

                                    <div
                                        className={`overflow-hidden transition-[max-height,opacity] duration-500 ${
                                            expanded ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
                                        }`}
                                    >
                                        <div className="mt-5 pt-4 border-t border-slate-200 font-mono text-[11px] space-y-1.5">
                                            <div className="text-cyan-600 font-bold">DOSSIER // {exp.id}</div>
                                            {exp.dossier.map(([k, v]) => (
                                                <div key={k} className="flex justify-between text-slate-500">
                                                    <span>{k}</span>
                                                    <span className="text-slate-700">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full mt-6 pt-4 border-t border-slate-200 font-mono text-[11px] text-cyan-600 flex items-center justify-between">
                                    <span>{exp.footer}</span>
                                    <ChevronDown
                                        className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-300 ${
                                            expanded ? "rotate-180" : ""
                                        }`}
                                    />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
